import { Link } from "react-router-dom";
import { businessProfile } from "../../../mocks/businessData";

export type BusinessSection =
  | "profile"
  | "gallery"
  | "campaigns"
  | "support"
  | "brochure"
  | "advertise"
  | "contact";

interface BusinessSidebarProps {
  active: BusinessSection;
  onNavigate: (s: BusinessSection) => void;
  collapsed: boolean;
  onToggle: () => void;
  openTickets: number;
}

const NAV_ITEMS: { id: BusinessSection; label: string; icon: string; badge?: number }[] = [
  { id: "profile",   label: "İşletme Profilim",  icon: "ri-building-line" },
  { id: "gallery",   label: "Görseller & Galeri", icon: "ri-image-line" },
  { id: "campaigns", label: "Kampanyalar",        icon: "ri-coupon-line" },
  { id: "support",   label: "Destek Al",          icon: "ri-customer-service-2-line" },
  { id: "brochure",  label: "Broşür Talep Et",    icon: "ri-file-text-line" },
  { id: "advertise", label: "Reklam Ver",         icon: "ri-megaphone-line" },
  { id: "contact",   label: "Yönetici İletişim",  icon: "ri-mail-send-line" },
];

export default function BusinessSidebar({ active, onNavigate, collapsed, onToggle, openTickets }: BusinessSidebarProps) {
  return (
    <aside className={`flex flex-col bg-white border-r border-stone-100 transition-all duration-300 flex-shrink-0 ${collapsed ? "w-16" : "w-64"} min-h-screen`}>
      {/* Header */}
      <div className={`flex items-center gap-3 px-4 h-16 border-b border-stone-100 flex-shrink-0 ${collapsed ? "justify-center" : ""}`}>
        <div className="w-8 h-8 rounded-xl bg-emerald-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-compass-3-line text-white text-base" />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-stone-800 truncate">BursaGuide360</div>
            <div className="text-xs text-stone-400 truncate">İşletme Paneli</div>
          </div>
        )}
        {!collapsed && (
          <button onClick={onToggle} className="w-6 h-6 flex items-center justify-center text-stone-300 hover:text-stone-600 transition-colors cursor-pointer flex-shrink-0">
            <i className="ri-sidebar-fold-line text-base" />
          </button>
        )}
      </div>

      {collapsed && (
        <button onClick={onToggle} className="flex items-center justify-center py-3 text-stone-300 hover:text-stone-600 transition-colors cursor-pointer">
          <i className="ri-sidebar-unfold-line text-lg" />
        </button>
      )}

      {/* Business Quick Info */}
      {!collapsed && (
        <div className="px-4 py-4 border-b border-stone-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100">
              <img src={businessProfile.coverImage} alt="" className="w-full h-full object-cover object-top" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-stone-800 truncate">{businessProfile.name}</p>
              <div className="flex items-center gap-1 mt-0.5">
                <i className="ri-star-fill text-amber-500 text-xs" />
                <span className="text-xs text-stone-500">{businessProfile.rating} · {businessProfile.reviews.toLocaleString()} yorum</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${businessProfile.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${businessProfile.isPublished ? "bg-emerald-500" : "bg-stone-300"}`} />
              {businessProfile.isPublished ? "Yayında" : "Taslak"}
            </span>
            {businessProfile.isSponsored && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Sponsorlu</span>
            )}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const isActive = active === item.id;
          const badge = item.id === "support" ? openTickets : undefined;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              title={collapsed ? item.label : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                isActive
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-100"
                  : "text-stone-500 hover:text-stone-800 hover:bg-stone-50"
              } ${collapsed ? "justify-center" : ""}`}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base ${isActive ? "text-emerald-700" : ""}`} />
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">{item.label}</span>
                  {badge !== undefined && badge > 0 && (
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
                      {badge}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className={`p-3 border-t border-stone-100 ${collapsed ? "flex justify-center" : ""}`}>
        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-stone-50 mb-2">
            <div className="w-8 h-8 rounded-full bg-emerald-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {businessProfile.ownerAvatar}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-stone-800 truncate">{businessProfile.ownerName}</p>
              <p className="text-xs text-stone-400 truncate">İşletme Sahibi</p>
            </div>
          </div>
        )}
        <Link
          to="/"
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-stone-400 hover:text-stone-700 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap ${collapsed ? "justify-center" : ""}`}
        >
          <i className="ri-external-link-line text-sm" />
          {!collapsed && "Siteye Dön"}
        </Link>
      </div>
    </aside>
  );
}
